import { createFileRoute } from "@tanstack/react-router";
import { useSite } from "@/components/site/site-data";
import { PageHeader } from "@/components/site/PageHeader";
import { parseParticipants } from "@/components/admin/ParticipantsEditor";
import type { AgendaParticipant } from "@/lib/types";

export const Route = createFileRoute("/agenda")({
  head: () => ({
    meta: [
      { title: "Agenda Conferinței — InvesTenergy 2026" },
      {
        name: "description",
        content:
          "Agenda Conferinței anuale InvesTenergy 2026 și a Galei Specialiștilor din Energie, 6 octombrie 2026, Le Château, București.",
      },
      { property: "og:title", content: "Agenda Conferinței — InvesTenergy 2026" },
      {
        property: "og:description",
        content: "Programul sesiunilor din 6 octombrie 2026, Le Château, București.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Agenda,
});

function Agenda() {
  const { agenda } = useSite();

  return (
    <main>
      <PageHeader title="Agendă" subtitle="6 octombrie 2026 · Le Château, București" />
      <section className="mx-auto max-w-6xl px-6 py-16">
        {agenda.length === 0 ? (
          <div className="mt-12 rounded-xl border border-dashed p-10 text-center">
            <p className="font-display text-xl font-bold">Agenda va fi publicată în curând</p>
            <p className="mt-2 text-muted-foreground">
              Reveniți pe această pagină pentru programul detaliat al sesiunilor.
            </p>
          </div>
        ) : (
          <ol className="mt-12 border-l">
            {agenda.map((item) => (
              <li key={item.id} className="relative pb-10 pl-8">
                <span className="absolute -left-[6px] top-2 size-3 rounded-full bg-primary" />
                <p className="font-display text-sm font-bold text-primary">
                  {item.start_time}
                  {item.end_time ? ` – ${item.end_time}` : ""}
                </p>
                <h3 className="mt-1 text-xl font-bold">{item.title}</h3>
                {item.description && (
                  <p className="mt-1 text-muted-foreground">{item.description}</p>
                )}
                <Participants list={parseParticipants(item.participants)} fallback={item.speakers} />
              </li>
            ))}
          </ol>
        )}
      </section>
    </main>
  );
}

const DEFAULT_LABEL: Record<string, string> = { moderator: "Moderator", comoderator: "Comoderator" };

function Participants({ list, fallback }: { list: AgendaParticipant[]; fallback: string | null }) {
  if (list.length === 0) {
    return fallback ? <p className="mt-2 text-sm text-muted-foreground">{fallback}</p> : null;
  }
  return (
    <div className="mt-4 space-y-2">
      {list.map((p) =>
        p.type === "group" ? (
          <p key={p.id} className="pt-3 text-sm font-bold uppercase tracking-wide text-primary">
            {p.name}
          </p>
        ) : (
          <p key={p.id} className="leading-snug">
            {p.type !== "speaker" && (
              <span className="mr-1 font-semibold text-primary">{p.label || DEFAULT_LABEL[p.type]}:</span>
            )}
            <span className="font-bold text-foreground">{p.name}</span>
            {p.position && <span className="text-muted-foreground"> – {p.position}</span>}
          </p>
        ),
      )}
    </div>
  );
}
